/**
 * Image Asset team permissions (VistoeraTASK-00555) - DISPLAY side of the rules
 * the backend enforces (`image_team_service.can_manage` / `can_organize`).
 * Hiding a menu item here is UX only; every API re-checks server-side from the
 * real Workspace membership.
 *
 * Rules (Workspace role = canonical membership role, NOT a Platform role):
 *   - own image                        -> delete / visibility / move / archive
 *   - teammate's image, MEMBER         -> move / archive only
 *   - teammate's image, OWNER or ADMIN -> everything (shared images only)
 * Ownership is ALWAYS `item.created_by_user_id === authenticated user id` -
 * never a display name, avatar, "我" label or e-mail.
 */

export interface PermissionItemLike {
  created_by_user_id?: string | null
  visibility?: string | null
}

export interface PermissionTeamLike {
  enabled?: boolean
  /** authenticated user id (server-provided) */
  user_id?: string
  /** canonical membership role: owner | admin | editor | ... */
  role?: string
}

export interface ImageAssetPermissions {
  isCreator: boolean
  /** can view/organise (move, archive): the image is accessible to the caller */
  canOrganize: boolean
  canDelete: boolean
  canChangeVisibility: boolean
}

const MANAGER_ROLES = new Set(['owner', 'admin'])

export function isWorkspaceManagerRole(role: string | null | undefined): boolean {
  return MANAGER_ROLES.has(String(role ?? '').toLowerCase())
}

export function imageAssetPermissions(
  item: PermissionItemLike,
  team: PermissionTeamLike | null | undefined,
): ImageAssetPermissions {
  const none: ImageAssetPermissions = {
    isCreator: false,
    canOrganize: false,
    canDelete: false,
    canChangeVisibility: false,
  }
  const userId = team?.user_id
  if (!team || !userId) return none // unknown identity => deny (fail closed)
  const creatorId = item.created_by_user_id ?? null
  const isCreator = Boolean(creatorId) && creatorId === userId
  const shared = (item.visibility || 'workspace') === 'workspace'
  const manager = isWorkspaceManagerRole(team.role)
  // Legacy PRIVATE history with no recorded creator belongs to the Owner only.
  const legacyOwnerOnly = !creatorId && !shared && String(team.role ?? '').toLowerCase() === 'owner'
  const accessible = shared || isCreator || legacyOwnerOnly
  const canManage = isCreator || (shared && manager) || legacyOwnerOnly
  return {
    isCreator,
    canOrganize: accessible,
    canDelete: canManage,
    canChangeVisibility: canManage,
  }
}

/** Items of `items` the caller may NOT delete / re-share (for batch refusal). */
export function itemsLackingManage<T extends PermissionItemLike>(
  items: readonly T[],
  team: PermissionTeamLike | null | undefined,
): T[] {
  return items.filter((i) => !imageAssetPermissions(i, team).canDelete)
}

export function itemsLackingOrganize<T extends PermissionItemLike>(
  items: readonly T[],
  team: PermissionTeamLike | null | undefined,
): T[] {
  return items.filter((i) => !imageAssetPermissions(i, team).canOrganize)
}
